import type { ConversationClient, OpenedConversation } from "@/api/agent-conversation";
import type { ActivityNode } from "@/api/config-server-stages";
import type { RunForestNode } from "@/api/config-migration";
import { moduleConfigConversation } from "@/api/module-config";
import { orgStructureConversation, type OrgScriptMode } from "@/api/org-structure-conversation";
import { sscuiConversation } from "@/api/sscui-conversation";
import { conversationAdapter } from "@/components/agents/conversation/conversationAdapter";
import type { ConversationAdapter } from "@/components/agents/conversation/types";

/** O cliente de conversa de cada nó da etapa de execução (estrutura organizacional, SSCUI e App Fiori). */
export function conversationClientOf(node: ActivityNode | "org_structure"): ConversationClient {
  if (node === "org_structure") return orgStructureConversation;
  return node === "sscui" ? sscuiConversation : moduleConfigConversation;
}

/**
 * Religa à conversa aberta do projeto ou abre uma nova para a unidade da estrutura
 * organizacional. A unidade e os ancestrais vão como `context`: o nó não tem catálogo
 * no banco, a floresta vem da tela. A conversa religada (`resumed`) é a aberta no nó,
 * de qualquer unidade: o `activityCode` dela diz de quem é, e quem chama confere.
 * `scriptMode` diz o que o consultor decidiu sobre o roteiro pronto (`auto` por padrão).
 */
export function openOrgUnitConversation(
  projectId: string,
  unit: RunForestNode,
  ancestors: RunForestNode[],
  scriptMode: OrgScriptMode = "auto",
): Promise<OpenedConversation> {
  return orgStructureConversation.openOrResume(projectId, unit.item_key, {
    unit,
    ancestors,
    script_mode: scriptMode,
  });
}

/** Religa à conversa aberta no projeto ou abre uma nova para a configuração. */
export function openNodeConversation(
  projectId: string,
  node: ActivityNode,
  code: string,
): Promise<OpenedConversation> {
  return conversationClientOf(node).openOrResume(projectId, code);
}

export function buildNodeAdapter(
  projectId: string,
  node: ActivityNode | "org_structure",
  runId: string,
): ConversationAdapter {
  return conversationAdapter(conversationClientOf(node), projectId, runId);
}
